import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useModalManager } from '../../../../../hooks/ModalManager.hook';
import useJobsHook from '../../../../../hooks/useJobs.hook';

import BaseModalComponent from '../../../../../components/Modal/BaseModal.component';
import ButtonComponent, {
  StyledButton,
} from '../../../../../components/Button.component';
import SelectComponent, {
  SelectOptionProps,
} from '../../../../../components/Input/Select.component';
import UserBadgeComponent from '../../../../../components/UserBadge.component';
import { HR } from '../../../../../redux/Dashboard/HRVacancies/HRVacancies.type';
import TextareaComponent from '../../../../../components/Input/Textarea.component';
import { textAriasMaxLength } from '../../../../../constants/textArias.constant';
import useFormHook from '../../../../../hooks/useForm.hook';
import { required } from '../../../../../services/validator.service';

interface Props extends React.AllHTMLAttributes<HTMLDivElement> {
  sectionItem: HR;
  editSectionItem?: Function;
}

const HRVacancyEditModalComponent = ({
  sectionItem,
  editSectionItem,
}: Props) => {
  const { t } = useTranslation();
  const { closeModal } = useModalManager();
  const { getSelectList: getJobsList } = useJobsHook();
  const { form, registerField, handleFormChange, validate } = useFormHook();

  const handleCreateEdit = () => {
    if (!validate()) return;

    const payload = {
      user_id: sectionItem.user_id,
      job_id: form.job?.value,
      comment: form.comment,
    };
    editSectionItem && editSectionItem(payload);
    closeModal();
  };

  const renderFooter = useMemo(
    () => (
      <StyledFooterButtonContainer>
        <StyledFooterButtonGroup>
          <ButtonComponent handleClick={handleCreateEdit} customColor="#206FE2">
            {t('Save')}
          </ButtonComponent>
          <ButtonComponent handleClick={closeModal}>
            {t('Cancel')}
          </ButtonComponent>
        </StyledFooterButtonGroup>
      </StyledFooterButtonContainer>
    ),
    [handleCreateEdit],
  );

  return (
    <BaseModalComponent
      header={t('Add vacancies for HR')}
      footer={renderFooter}
    >
      <StyledHRInfoWrapper>
        <div>{t(`controlModal.hr.Selected hr`)}:</div>
        <UserBadgeComponent info={sectionItem as any} />
      </StyledHRInfoWrapper>

      <SelectComponent
        {...registerField('job', {
          validations: [required],
        })}
        value={form.job}
        label={t('List of vacancies')}
        placeholder={t('Choose job')}
        loadOptions={getJobsList}
        onChangeOption={(job: SelectOptionProps) => handleFormChange({ job })}
        useMenuPortalTarget
      />

      <TextareaComponent
        {...registerField('comment')}
        value={form.comment}
        label={t('Comment')}
        maxLength={textAriasMaxLength.EXTENDED}
        onChange={(comment: string) => handleFormChange({ comment })}
      />
    </BaseModalComponent>
  );
};

export default HRVacancyEditModalComponent;

export const StyledFooterButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 10px 0 0 0;
  gap: 10px;

  ${StyledButton} {
    min-width: 100px;
  }
`;

const StyledFooterButtonGroup = styled.div`
  display: flex;
  margin-left: auto;
  gap: 0.5rem;
`;

const StyledHRInfoWrapper = styled.div`
  display: flex;
  margin-bottom: 10px;

  > div:first-child {
    margin-right: 10px;
    font-weight: 500;
  }
`;
